import { useEffect, useState } from "react";

import { parseDetail } from "../utils/errors";
import { useMatch, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CopyableGuid from "../components/CopyableGuid";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningIcon from "@mui/icons-material/Warning";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import useRequests from "../hooks/http";
import { useTenant } from "../hooks/useTenant";

function RedirectUrisDrawer({
  open,
  uris,
  onChange,
  onClose,
  title,
}: {
  open: boolean;
  uris: string[];
  onChange: (uris: string[]) => void;
  onClose: () => void;
  title: string;
}) {
  const [draft, setDraft] = useState("");

  const handleAdd = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...uris, trimmed]);
    setDraft("");
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 420, p: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Only URIs registered here will be accepted by the authorisation server. Add every callback URL your client may use, including localhost addresses for local development.
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <TextField
          label="Redirect URI"
          fullWidth
          size="small"
          placeholder="https://example.com/callback"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button variant="contained" onClick={handleAdd} disabled={!draft.trim()} sx={{ minWidth: 40, px: 1 }}>
          <AddIcon />
        </Button>
      </Box>

      {uris.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No redirect URIs added yet.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {uris.map((uri, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 1.5,
                py: 1,
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: "break-all", mr: 1 }}>
                {uri}
              </Typography>
              <IconButton size="small" color="error" onClick={() => onChange(uris.filter((_, j) => j !== i))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Drawer>
  );
}

export default function ApplicationDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { get, post, patch, del } = useRequests();
  const navigate = useNavigate();

  const { activeTenant } = useTenant();
  const onPermissions = useMatch("/app/applications/:id/permissions");
  const onUsers = useMatch("/app/applications/:id/users");
  const tab = onUsers ? 2 : onPermissions ? 1 : 0;
  const [app, setApp] = useState<Application | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [allMembers, setAllMembers] = useState<TenantMember[]>([]);
  const [grantedMemberIds, setGrantedMemberIds] = useState<number[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<number | "">("");
  const [usersError, setUsersError] = useState("");
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", type: "confidential" as string, redirect_uris: [] as string[], description: "" });
  const [error, setError] = useState("");

  const [urisDrawerOpen, setUrisDrawerOpen] = useState(false);

  const [editClient, setEditClient] = useState<Client | null>(null);
  const [editForm, setEditForm] = useState({ name: "", description: "", redirect_uris: [] as string[] });
  const [editUrisDrawerOpen, setEditUrisDrawerOpen] = useState(false);
  const [editError, setEditError] = useState("");

  const [secretDialog, setSecretDialog] = useState(false);
  const [createdSecret, setCreatedSecret] = useState("");
  const [createdClientId, setCreatedClientId] = useState("");
  const [copied, setCopied] = useState(false);

  const [rotateTarget, setRotateTarget] = useState<Client | null>(null);
  const [rotatedSecret, setRotatedSecret] = useState("");

  const [deleteAppOpen, setDeleteAppOpen] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [revokeError, setRevokeError] = useState("");

  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permForm, setPermForm] = useState({ value: "", description: "" });
  const [permError, setPermError] = useState("");
  const [deletePermTarget, setDeletePermTarget] = useState<Permission | null>(null);

  useEffect(() => {
    Promise.all([
      get(`/admin/applications/${id}`),
      get(`/admin/applications/${id}/clients`),
    ]).then(([appRes, clientsRes]) => {
      if (appRes.status === 200) {
        setApp(appRes.data);
        const tenantId = appRes.data.tenant_id;
        Promise.all([
          get(`/admin/tenants/${tenantId}/applications/${id}/permissions`),
          get(`/admin/tenants/${tenantId}/users`),
          get(`/admin/tenants/${tenantId}/applications/${appRes.data.id}/members`),
        ]).then(([permRes, membersRes, grantedRes]) => {
          if (permRes.status === 200 && Array.isArray(permRes.data)) setPermissions(permRes.data);
          if (membersRes.status === 200 && Array.isArray(membersRes.data)) setAllMembers(membersRes.data);
          if (grantedRes.status === 200 && Array.isArray(grantedRes.data)) setGrantedMemberIds(grantedRes.data);
        });
      }
      if (clientsRes.status === 200 && Array.isArray(clientsRes.data)) setClients(clientsRes.data);
      setLoading(false);
    });
  }, [id, get]);

  const handleCreateClient = async () => {
    setError("");
    const redirect_uris = newClient.redirect_uris.length > 0 ? newClient.redirect_uris.join(",") : undefined;
    const { data, status } = await post(`/admin/applications/${id}/clients`, null, {
      name: newClient.name,
      type: newClient.type,
      description: newClient.description,
      redirect_uris,
    });
    if (status === 201 || status === 200) {
      setClients((prev) => [...prev, data]);
      setCreatedClientId(data.client_id);
      setCreatedSecret(data.client_secret);
      setCreateOpen(false);
      setSecretDialog(true);
      setNewClient({ name: "", type: "confidential", redirect_uris: [], description: "" });
    } else {
      setError(parseDetail(data?.detail, "Failed to create client"));
    }
  };

  const handleEditOpen = (client: Client) => {
    setEditClient(client);
    setEditForm({
      name: client.name,
      description: client.description ?? "",
      redirect_uris: client.redirect_uris ? client.redirect_uris.split(",").map((u) => u.trim()).filter(Boolean) : [],
    });
    setEditError("");
  };

  const handleEditSave = async () => {
    if (!editClient) return;
    setEditError("");
    const redirect_uris = editForm.redirect_uris.length > 0 ? editForm.redirect_uris.join(",") : null;
    const { data, status } = await patch(`/admin/clients/${editClient.id}`, {
      name: editForm.name,
      description: editForm.description,
      redirect_uris,
    });
    if (status === 200) {
      setClients((prev) => prev.map((c) => (c.id === editClient.id ? data : c)));
      setEditClient(null);
    } else {
      setEditError(parseDetail(data?.detail, "Failed to update client"));
    }
  };

  const handleRotate = async () => {
    if (!rotateTarget) return;
    const { data, status } = await post(`/admin/clients/${rotateTarget.id}/rotate`);
    if (status === 200) {
      setRotatedSecret(data.client_secret);
      setCreatedClientId(rotateTarget.client_id);
      setRotateTarget(null);
      setSecretDialog(true);
    }
  };

  const handleDeactivate = async (clientId: number) => {
    setRevokeError("");
    const { data, status } = await del(`/admin/clients/${clientId}`);
    if (status === 200 || status === 204) {
      setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, active: false } : c)));
    } else {
      setRevokeError(parseDetail(data?.detail, `Failed to revoke client (status ${status})`));
    }
  };

  const handleActivate = async (clientId: number) => {
    setRevokeError("");
    const { data, status } = await post(`/admin/clients/${clientId}/activate`);
    if (status === 200) {
      setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, active: true } : c)));
    } else {
      setRevokeError(parseDetail(data?.detail, `Failed to re-activate client (status ${status})`));
    }
  };

  const handleDeleteClient = async (clientId: number) => {
    setRevokeError("");
    const { data, status } = await del(`/admin/clients/${clientId}/permanent`);
    if (status === 204 || status === 200) {
      setClients((prev) => prev.filter((c) => c.id !== clientId));
    } else {
      setRevokeError(parseDetail(data?.detail, `Failed to delete client (status ${status})`));
    }
  };

  const handleCreatePermission = async () => {
    if (!app) return;
    setPermError("");
    const { data, status } = await post(
      `/admin/tenants/${app.tenant_id}/applications/${id}/permissions`,
      null,
      permForm,
    );
    if (status === 201 || status === 200) {
      setPermissions((prev) => [...prev, data]);
      setPermDialogOpen(false);
      setPermForm({ value: "", description: "" });
    } else {
      setPermError(parseDetail(data?.detail, "Failed to create permission"));
    }
  };

  const handleDeletePermission = async () => {
    if (!deletePermTarget) return;
    const { status } = await del(`/admin/permissions/${deletePermTarget.id}`);
    if (status === 204 || status === 200) {
      setPermissions((prev) => prev.filter((p) => p.id !== deletePermTarget.id));
      setDeletePermTarget(null);
    }
  };

  const handleToggleSuspend = async () => {
    setSuspending(true);
    const { data, status } = await patch(`/admin/applications/${id}`, { active: !app!.active });
    if (status === 200) {
      setApp(data);
      setClients((prev) => prev.map((c) => ({ ...c, active: data.active ? c.active : false })));
    }
    setSuspending(false);
  };

  const handleToggleRbac = async () => {
    if (!app) return;
    const { data, status } = await patch(`/admin/applications/${id}`, {
      active: app.active,
      rbac_enabled: !app.rbac_enabled,
    });
    if (status === 200) {
      setApp(data);
    }
  };

  const handleDeleteApp = async () => {
    const { status } = await del(`/admin/applications/${id}`);
    if (status === 200 || status === 204) {
      navigate(-1);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const uriCount = newClient.redirect_uris.length;

  if (loading) return <LinearProgress />;
  if (!app) return <Alert severity="error">Application not found</Alert>;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography variant="h4">{app.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {app.identifier}
          </Typography>
          <Typography variant="body1" sx={{ mt: 1 }}>
            {app.description}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t("applications.detail.description")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label={app.active ? "Active" : "Suspended"} color={app.active ? "success" : "warning"} />
          <Button
            variant="outlined"
            size="small"
            color={app.active ? "warning" : "success"}
            disabled={suspending}
            onClick={handleToggleSuspend}
          >
            {app.active ? "Suspend" : "Restore"}
          </Button>
          <Button color="error" variant="outlined" size="small" onClick={() => setDeleteAppOpen(true)}>
            Delete
          </Button>
        </Box>
      </Box>

      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <CopyableGuid label="GUID" value={app.guid} />
        {app.version && (
          <Typography variant="body2" color="text.secondary">
            Version: {app.version}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Created: {new Date(app.created).toLocaleString()}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={app.rbac_enabled}
                onChange={handleToggleRbac}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                RBAC {app.rbac_enabled ? "enabled" : "disabled"}
              </Typography>
            }
          />
          <Typography
            variant="caption"
            color={app.rbac_enabled ? "text.secondary" : "warning.main"}
            sx={{ display: "block", mt: 0.5 }}
          >
            {t(app.rbac_enabled ? "applications.detail.rbacEnabled" : "applications.detail.rbacDisabled")}
          </Typography>
        </Box>
      </Card>

      <Tabs
        value={tab}
        onChange={(_, v) => {
          const paths = ["clients", "permissions", "users"];
          navigate(`/app/applications/${id}/${paths[v]}`);
        }}
        sx={{ mb: 3 }}
      >
        <Tab label={`OAuth Clients (${clients.length})`} />
        <Tab label={`Permissions (${permissions.length})`} />
        <Tab label={`Users (${grantedMemberIds.length})`} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h5">{t("applications.detail.tabs.clients")}</Typography>
            <Button variant="contained" onClick={() => setCreateOpen(true)}>
              {t("applications.detail.clients.newButton")}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("applications.detail.clients.description")}
          </Typography>
          {revokeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRevokeError("")}>
              {revokeError}
            </Alert>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Secret</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Redirect URIs</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>{client.name}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" fontFamily="monospace">
                          {client.client_id}
                        </Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(client.client_id)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace" color="text.secondary">
                        {client.secret_hint ? `••••••••${client.secret_hint}` : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={client.type} />
                    </TableCell>
                    <TableCell>
                      {client.redirect_uris ? (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {client.redirect_uris.split(",").map((uri, i) => (
                            <Tooltip key={i} title={uri.trim()} placement="top">
                              <Chip
                                size="small"
                                label={new URL(uri.trim()).host}
                                variant="outlined"
                                sx={{ fontFamily: "monospace", maxWidth: 160 }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">None</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={client.active ? "Active" : "Revoked"}
                        color={client.active ? "success" : "error"}
                      />
                    </TableCell>
                    <TableCell>{new Date(client.created).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {client.active ? (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" onClick={() => handleEditOpen(client)}>
                            Edit
                          </Button>
                          <Button size="small" onClick={() => setRotateTarget(client)}>
                            Rotate
                          </Button>
                          <Button size="small" color="error" onClick={() => handleDeactivate(client.id)}>
                            Revoke
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button size="small" color="success" onClick={() => handleActivate(client.id)}>
                            Restore
                          </Button>
                          <Button size="small" color="error" onClick={() => handleDeleteClient(client.id)}>
                            Delete
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {clients.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No clients registered for this application.
            </Typography>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h5">{t("applications.detail.tabs.permissions")}</Typography>
            <Button
              variant="contained"
              onClick={() => {
                setPermForm({ value: "", description: "" });
                setPermError("");
                setPermDialogOpen(true);
              }}
            >
              {t("applications.detail.permissions.newButton")}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("applications.detail.permissions.description")}
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Value</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions.map((perm) => (
                  <TableRow key={perm.id}>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {perm.value}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {perm.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>{new Date(perm.created).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Button size="small" color="error" onClick={() => setDeletePermTarget(perm)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {permissions.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No permissions defined for this application.
            </Typography>
          )}
        </Box>
      )}

      {tab === 2 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography variant="h5">Users</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Users who have been granted access to this application. You can add or remove users here.
          </Typography>
          {usersError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUsersError("")}>
              {usersError}
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
            <FormControl fullWidth size="small" disabled={allMembers.filter((m) => m.active && !grantedMemberIds.includes(m.id)).length === 0}>
              <InputLabel>Grant access</InputLabel>
              <Select
                value={selectedMemberId}
                label="Grant access"
                onChange={(e) => setSelectedMemberId(e.target.value as number | "")}
              >
                {allMembers
                  .filter((m) => m.active && !grantedMemberIds.includes(m.id))
                  .map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.first_name} {m.last_name} ({m.username})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={async () => {
                if (!app || selectedMemberId === "") return;
                setUsersError("");
                const { status: s, data } = await post(
                  `/admin/tenants/${app.tenant_id}/users/${selectedMemberId}/access`,
                  null,
                  { application_id: app.id },
                );
                if (s === 204 || s === 200 || s === 201) {
                  setGrantedMemberIds((prev) => [...prev, selectedMemberId as number]);
                  setSelectedMemberId("");
                } else {
                  setUsersError(parseDetail(data?.detail, "Failed to grant access"));
                }
              }}
              disabled={selectedMemberId === ""}
              sx={{ minWidth: 72 }}
            >
              Grant
            </Button>
          </Box>
          {allMembers.filter((m) => m.active && !grantedMemberIds.includes(m.id)).length === 0 && grantedMemberIds.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              All active users have been granted access.
            </Typography>
          )}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allMembers
                  .filter((m) => grantedMemberIds.includes(m.id))
                  .map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>
                        {member.first_name} {member.last_name}
                      </TableCell>
                      <TableCell>{member.username}</TableCell>
                      <TableCell>{member.email || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={member.active ? "Active" : "Inactive"}
                          color={member.active ? "success" : "default"}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          onClick={async () => {
                            if (!app) return;
                            setUsersError("");
                            const { status: s, data } = await del(
                              `/admin/tenants/${app.tenant_id}/users/${member.id}/access/${app.id}`,
                            );
                            if (s === 204 || s === 200) {
                              setGrantedMemberIds((prev) => prev.filter((mid) => mid !== member.id));
                            } else {
                              setUsersError(parseDetail(data?.detail, "Failed to revoke access"));
                            }
                          }}
                        >
                          Revoke
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          {grantedMemberIds.length === 0 && (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No users have been granted access to this application.
            </Typography>
          )}
        </Box>
      )}

      {/* Create Client Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New OAuth Client</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newClient.name}
            onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={newClient.description}
            onChange={(e) => setNewClient({ ...newClient, description: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={newClient.type}
              label="Type"
              onChange={(e) => setNewClient({ ...newClient, type: e.target.value })}
            >
              <MenuItem value="confidential">Confidential</MenuItem>
              <MenuItem value="public">Public</MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, mb: 1 }}>
            {t("applications.detail.clients.typeHelp")}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setUrisDrawerOpen(true)}
              startIcon={<AddIcon />}
            >
              Redirect URIs{uriCount > 0 ? ` (${uriCount})` : ""}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {t("applications.detail.clients.redirectUriHelp")}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateClient} disabled={!newClient.name}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <RedirectUrisDrawer
        open={urisDrawerOpen}
        title="Redirect URIs"
        uris={newClient.redirect_uris}
        onChange={(uris) => setNewClient((prev) => ({ ...prev, redirect_uris: uris }))}
        onClose={() => setUrisDrawerOpen(false)}
      />

      {/* Edit Client Drawer */}
      <Drawer anchor="right" open={Boolean(editClient)} onClose={() => setEditClient(null)} PaperProps={{ sx: { width: 420, p: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Edit Client</Typography>
          <IconButton onClick={() => setEditClient(null)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 3 }} />
        {editError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {editError}
          </Alert>
        )}
        <TextField
          label="Name"
          fullWidth
          sx={{ mb: 2 }}
          value={editForm.name}
          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
        />
        <TextField
          label="Description"
          fullWidth
          sx={{ mb: 3 }}
          value={editForm.description}
          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => setEditUrisDrawerOpen(true)}
          startIcon={<AddIcon />}
          sx={{ mb: 3 }}
        >
          Redirect URIs{editForm.redirect_uris.length > 0 ? ` (${editForm.redirect_uris.length})` : ""}
        </Button>
        <Button variant="contained" fullWidth onClick={handleEditSave} disabled={!editForm.name}>
          Save
        </Button>
      </Drawer>

      <RedirectUrisDrawer
        open={editUrisDrawerOpen}
        title="Redirect URIs"
        uris={editForm.redirect_uris}
        onChange={(uris) => setEditForm((prev) => ({ ...prev, redirect_uris: uris }))}
        onClose={() => setEditUrisDrawerOpen(false)}
      />

      {/* Rotate Confirmation Dialog */}
      <Dialog open={Boolean(rotateTarget)} onClose={() => setRotateTarget(null)}>
        <DialogTitle>Rotate Client Secret</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
            <WarningIcon color="warning" sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography>
                This will invalidate the current secret for <strong>{rotateTarget?.name}</strong>. Any service using the
                old secret will stop working immediately.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("applications.detail.clients.rotateHelp")}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateTarget(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleRotate}>
            Rotate Secret
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Application Dialog */}
      <Dialog open={deleteAppOpen} onClose={() => setDeleteAppOpen(false)}>
        <DialogTitle>Delete Application</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningIcon color="error" />
            <Typography>
              This will deactivate <strong>{app.name}</strong> and all its OAuth clients. This cannot be undone.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteAppOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteApp}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Permission Dialog */}
      <Dialog open={permDialogOpen} onClose={() => setPermDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Permission</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("applications.detail.permissions.description")}
          </Typography>
          {permError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {permError}
            </Alert>
          )}
          <TextField
            label="Value"
            fullWidth
            margin="normal"
            placeholder="e.g. read:orders"
            helperText={t("applications.detail.permissions.valueHelp")}
            value={permForm.value}
            onChange={(e) => setPermForm({ ...permForm, value: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={2}
            value={permForm.description}
            onChange={(e) => setPermForm({ ...permForm, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePermission} disabled={!permForm.value}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Permission Confirmation */}
      <Dialog open={Boolean(deletePermTarget)} onClose={() => setDeletePermTarget(null)}>
        <DialogTitle>Delete Permission</DialogTitle>
        <DialogContent>
          <Typography>
            Permanently delete <strong>{deletePermTarget?.value}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePermTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeletePermission}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Secret Display Dialog */}
      <Dialog open={secretDialog} onClose={() => setSecretDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Client Credentials</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            The client secret is shown only once. Copy it now and store it securely.
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Client ID
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                {createdClientId}
              </Typography>
              <IconButton size="small" onClick={() => copyToClipboard(createdClientId)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Client Secret
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography fontFamily="monospace" sx={{ wordBreak: "break-all" }}>
                {createdSecret || rotatedSecret}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy"}>
                <IconButton size="small" onClick={() => copyToClipboard(createdSecret || rotatedSecret)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setSecretDialog(false);
              setCreatedSecret("");
              setRotatedSecret("");
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
